import { isSensitiveKey } from '@client-shared/utils/raygun-sensitive-keys.js'

// axios serializes a request body to a JSON string on config.data; only these methods carry one
const METHODS_WITH_BODY = [
  'post',
  'put',
  'patch',
]

// Deep-redact sensitive keys ourselves (defense-in-depth; independent of raygun's filter internals)
const redactSensitiveData = (value) => {
  if (Array.isArray(value)) {
    return value.map(redactSensitiveData)
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .map(([
          key,
          val,
        ]) => [
          key,
          isSensitiveKey(key) ? '[removed by filter]' : redactSensitiveData(val),
        ]),
    )
  }

  return value
}

// axios keeps the request body as a serialized JSON string on config.data – parse it back to an object
const parseRequestBody = (config) => {
  if (!METHODS_WITH_BODY.includes(config?.method?.toLowerCase()) || typeof config?.data !== 'string') {
    return undefined
  }

  try {
    return JSON.parse(config.data)
  } catch {
    // non-JSON body (e.g. url-encoded): we can't redact inside an opaque string, so never log it raw
    return '[non-JSON body omitted]'
  }
}

// Some URLs carry a credential rather than the body — download.api.js builds
// `?auth_token_restricted=<jwt>` straight into config.url — and redactSensitiveData is key-based, so
// it never looks inside a URL. Redact sensitive query VALUES, and credential-bearing path segments,
// before either export can report a URL.
const redactUrl = (url) => {
  const queryStart = url.indexOf('?')

  if (queryStart === -1) {
    return redactUrlPath(url)
  }

  const path = redactUrlPath(url.slice(0, queryStart))
  const query = url.slice(queryStart + 1)
    .split('&')
    .map((pair) => {
      const separator = pair.indexOf('=')

      if (separator === -1) {
        return pair
      }

      const key = pair.slice(0, separator)

      return isSensitiveKey(key) ? `${key}=[removed by filter]` : pair
    })
    .join('&')

  return `${path}?${query}`
}

const OBJECT_ID_REGEX = /^[0-9a-f]{24}$/i
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Credentials also ride in PATH segments, where redactUrlQuery cannot see them: the account
// confirmation token is a crypto.randomUUID() and travels as
// `userregistrations/validate/<email>/<token>` (user-registrations.api.js). It is the only UUID
// segment the API layer ever sends, so redacting UUID segments costs no diagnostic value while
// keeping that token out of raygun. ObjectId segments are entity ids, not secrets, and stay.
const redactUrlPath = (path) =>
  path.split('/')
    .map((segment) => (UUID_REGEX.test(segment) ? '[removed by filter]' : segment))
    .join('/')

// The headline below becomes Details.Error.Message, which raygun groups on — so the id segments have
// to collapse, otherwise every project/plan/document gets a report group of its own. The exact path
// stays available as requestUrl in the custom data.
const normalizeUrlForGrouping = (url) => {
  const [ path ] = url.split('?')

  return path.split('/')
    .map((segment) => (OBJECT_ID_REGEX.test(segment) || UUID_REGEX.test(segment) ? ':id' : segment))
    .join('/')
}

// Map an AxiosError onto a flat, raygun-friendly object using axios' own field names.
// requestMethod/requestBody are also read by the onBeforeSend hook in each app's raygun.js plugin
// to fill raygun's native "HTTP method" / "form values" request fields.
export const getAxiosCustomData = (error) =>
  redactSensitiveData({
    code: error.code, // ERR_BAD_REQUEST, ECONNABORTED, ERR_NETWORK, …
    httpStatus: error.response?.status,
    statusText: error.response?.statusText,
    requestMethod: error.config?.method,
    requestUrl: error.config?.url ? redactUrl(error.config.url) : undefined,
    requestBody: parseRequestBody(error.config),
    responseData: error.response?.data,
  })

// Readable one-line headline. The onBeforeSend hook in each app's raygun.js plugin promotes this to
// Details.Error.Message, so it is both the report title and the grouping key — axios' own message
// ("Request failed with status code 500") is identical for every endpoint that failed the same way.
export const getAjaxErrorMessage = (error) => {
  const method = error.config?.method?.toUpperCase() || 'REQUEST'
  const url = error.config?.url ? normalizeUrlForGrouping(error.config.url) : 'unknown'

  return error.response
    ? `AJAX Error: ${error.response.status} ${error.response.statusText} ${method} ${url}`
    : `AJAX Error: ${error.code || 'network error'} ${method} ${url}`
}
